import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

// TypewriterText reveals its text word by word, chatbot-style. The animation
// targets ~TYPEWRITER_TOTAL_MS total regardless of length, clamped so short
// texts don't blink past and long ones don't crawl. Used for the AI answer-check
// retry messages (see docs/pa-chief-complaint-realtime-check.md).
export const TYPEWRITER_TOTAL_MS = 3500;
const MIN_TICK_MS = 50;
const MAX_TICK_MS = 200;

export function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" && // jsdom has no matchMedia
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

// visibleSlice returns the prefix of text containing the first count words,
// with the original separators (including \n\n paragraph breaks) intact —
// join(" ") would collapse them.
function visibleSlice(text, count) {
  if (count <= 0) {
    return "";
  }
  const parts = text.split(/(\s+)/);
  let words = 0;
  let out = "";
  for (const part of parts) {
    const isWord = part !== "" && !/^\s+$/.test(part);
    out += part;
    if (isWord) {
      words += 1;
      if (words >= count) {
        break;
      }
    }
  }
  return out;
}

export default function TypewriterText({ text }) {
  const [visibleCount, setVisibleCount] = useState(0);

  // Keyed on text: a NEW guidance message restarts the reveal, while re-renders
  // with the same text (e.g. the dedupe path re-submitting an unchanged answer)
  // leave the fully-revealed text alone.
  useEffect(() => {
    const wordCount = text.split(/\s+/).filter(Boolean).length;
    if (prefersReducedMotion() || wordCount <= 1) {
      setVisibleCount(wordCount);
      return undefined;
    }
    setVisibleCount(0);
    const tickMs = Math.min(
      MAX_TICK_MS,
      Math.max(MIN_TICK_MS, Math.round(TYPEWRITER_TOTAL_MS / wordCount)),
    );
    const timer = setInterval(() => {
      setVisibleCount((n) => {
        if (n + 1 >= wordCount) {
          clearInterval(timer);
        }
        return n + 1;
      });
    }, tickMs);
    return () => clearInterval(timer);
  }, [text]);

  return (
    // Screen readers get the full text at once via aria-label; the animated
    // copy is aria-hidden so it isn't re-announced word by word.
    <span role="status" aria-label={text}>
      {/* The invisible full text reserves the final height up front, so the
          box doesn't grow line by line as words appear. */}
      <span
        aria-hidden="true"
        style={{
          visibility: "hidden",
          display: "block",
          position: "relative",
          whiteSpace: "pre-line",
        }}
      >
        {text}
        <span
          style={{
            visibility: "visible",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            whiteSpace: "pre-line",
          }}
        >
          {visibleSlice(text, visibleCount)}
        </span>
      </span>
    </span>
  );
}

TypewriterText.propTypes = {
  text: PropTypes.string.isRequired,
};
